import React, { useState } from 'react';
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { useIndexedUserData } from '../hooks/useIndexedUserData';
import { IndexedUserData, UserAnalyticsSortField } from '../types/indexedUser';
import { UserData } from '../types/user';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ArrowDownTrayIcon, ArrowPathIcon, CalendarDaysIcon, ChartBarIcon, ChevronUpIcon, ClockIcon, Cog6ToothIcon, DevicePhoneMobileIcon, ExclamationCircleIcon, EyeIcon, FunnelIcon, MagnifyingGlassIcon, Square2StackIcon, XMarkIcon } from '@heroicons/react/24/outline';
import UserDetailSidebar from '../components/UserDetailSidebar';
import { Link } from 'react-router-dom';
import { getResourceDisplayName, getResourceIcon } from '../utils/wearableUtils';

const UserAnalyticsPage: React.FC = () => {
    const {
        users,
        loading,
        error,
        refreshData,
        sortField,
        sortDirection,
        setSortField,
        setSortDirection,
        filter,
        setFilter,
        filteredUsers,
        stats,
    } = useIndexedUserData();

    const [indexingInProgress, setIndexingInProgress] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedUser, setSelectedUser] = useState<IndexedUserData | null>(null);

    const handleSort = (field: UserAnalyticsSortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            const numericFields: UserAnalyticsSortField[] = [
                'dayPostsCount',
                'postsCount',
                'aiChatsCount',
                'wearableDevicesCount',
                'trainingLogsCount',
                'activityLogsCount',
                'sessionsCount',
            ];
            setSortDirection(numericFields.includes(field) ? 'desc' : 'asc');
        }
    };

    const handleRunIndexing = async () => {
        if (!window.confirm('This will re-index all user data. This may take a few minutes. Continue?')) {
            return;
        }

        setIndexingInProgress(true);
        try {
            const functions = getFunctions();
            const runUserDataIndexing = httpsCallable(functions, 'runUserDataIndexing', { timeout: 600000 });
            const result = await runUserDataIndexing();
            const data = result.data as {
                success: boolean;
                totalUsers: number;
                indexedCount: number;
                errorCount: number;
                deletedCount: number;
                errors: Array<{ userId: string; error: string }>;
                message: string;
            };

            console.log('[INDEXING RESULT]', data);

            alert(`${data.message}\n\nRefreshing data...`);
            await refreshData();

            if (data.errorCount > 0 && data.errors && data.errors.length > 0) {
                console.error('[INDEXING ERRORS]', data.errors);
                const errorSamples = data.errors.slice(0, 5).map(e => `- ${e.userId}: ${e.error}`).join('\n');
                alert(`Indexing had ${data.errorCount} errors:\n\n${errorSamples}\n\nSee console for full details.`);
            }
        } catch (err) {
            console.error('Indexing failed:', err);
            alert(`Indexing failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
        } finally {
            setIndexingInProgress(false);
        }
    };

    const formatDate = (date: Date | null) => {
        if (!date) return 'N/A';
        if (!(date instanceof Date) || isNaN(date.getTime())) return 'Invalid Date';
        try {
            return new Intl.DateTimeFormat('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }).format(date);
        } catch {
            return 'Invalid Date';
        }
    };

    const exportUsersAsCSV = (usersToExport: IndexedUserData[], filenamePrefix: string) => {
        const headers = [
            '#', 'Name', 'Surname', 'Email', 'Username', 'UID', 'Role', 'Sport',
            'Subscription Type', 'Subscription Via', 'Day Posts', 'Posts', 'AI Chats',
            'Wearable Devices', 'Training Logs', 'Activity Logs', 'Sessions', 'Last Indexed',
        ];

        const rows = usersToExport.map((user, index) => [
            (index + 1).toString(),
            user.name || '', user.surname || '', user.email || '', user.username || '',
            user.id || '', user.role || '', user.sport || '',
            user.subscriptionType || 'default', user.subscriptionVia || 'default',
            user.dayPostsCount.toString(), user.postsCount.toString(),
            user.aiChatsCount.toString(), user.wearableDevicesCount.toString(),
            user.trainingLogsCount.toString(), user.activityLogsCount.toString(),
            user.sessionsCount.toString(), formatDate(user.lastIndexedAt),
        ]);

        const escapeCSVField = (field: string) => {
            const stringField = field ?? '';
            if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
                return `"${stringField.replace(/"/g, '""')}"`;
            }
            return stringField;
        };

        const csvContent = [
            headers.map(escapeCSVField).join(','),
            ...rows.map((row) => row.map(escapeCSVField).join(',')),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${filenamePrefix}-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadCSV = () => exportUsersAsCSV(filteredUsers, 'user-analytics');

    const downloadTopEngagedCSV = () => {
        const topEngaged = [...users]
            .sort((a, b) => b.dayPostsCount - a.dayPostsCount)
            .slice(0, 100);
        exportUsersAsCSV(topEngaged, 'top-100-engaged-users');
    };

    const downloadGarminUsersCSV = () => {
        const garminUsers = users.filter((user) =>
            user.wearableDevices?.some((device) => device.resource?.toLowerCase().includes('garmin'))
        );

        const headers = ['#', 'Name', 'Surname', 'Email'];
        const rows = garminUsers.map((user, index) => [
            (index + 1).toString(),
            user.name || '', user.surname || '', user.email || '',
        ]);

        const escapeCSVField = (field: string) => {
            const stringField = field ?? '';
            if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
                return `"${stringField.replace(/"/g, '""')}"`;
            }
            return stringField;
        };

        const csvContent = [
            headers.map(escapeCSVField).join(','),
            ...rows.map((row) => row.map(escapeCSVField).join(',')),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `garmin-users-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const clearFilters = () => {
        setFilter({});
    };

    const hasActiveFilters =
        filter.searchTerm ||
        filter.subscriptionType ||
        filter.subscriptionVia ||
        filter.hasWearable !== undefined ||
        filter.minDayPosts !== undefined;

    if (loading && filteredUsers.length === 0) {
        return (
            <DashboardWrapper>
                <div className="w-full h-[calc(100vh-64px)] flex flex-col items-center justify-center gap-4 md:gap-6 p-4 md:p-6">
                        <div className="text-center flex flex-col items-center justify-center gap-4">
                            <svg className="size-6 animate-spin text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-10" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <p className="text-gray-600">Loading indexed user data...</p>
                        </div>
                </div>
            </DashboardWrapper>
        );
    }

    if (!loading && stats.totalUsers === 0) {
        return (
            <DashboardWrapper>
                <div className="w-full flex flex-col gap-4 md:gap-6 p-4 md:p-6">
                    <h1 className="text-2xl font-semibold">User Analytics</h1>
                    <div className="bg-white rounded-xl border border-warning p-8 text-center">
                        <Cog6ToothIcon className="w-16 h-16 mx-auto text-warning mb-4" />
                        <h2 className="text-2xl font-bold mb-2">No Indexed Data Found</h2>
                        <p className="text-gray-600 mb-4 max-w-lg mx-auto">
                            The User Analytics page uses pre-indexed data for fast loading.
                            You need to run the indexing process first to populate this data.
                        </p>
                        <div className="bg-gray-50 rounded-lg p-4 mb-6 max-w-xl mx-auto text-left">
                            <h3 className="font-semibold mb-2">Steps to get started:</h3>
                            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                                <li>Make sure the Firebase functions are deployed:
                                    <code className="block ml-6 mt-1 bg-gray-200 px-2 py-1 rounded text-xs">
                                        cd functions && npm run build && firebase deploy --only functions
                                    </code>
                                </li>
                                <li>Click the "Run Indexing" button below to index all user data</li>
                                <li>Wait for the indexing to complete (may take a few minutes for many users)</li>
                            </ol>
                        </div>
                        <button
                            onClick={handleRunIndexing}
                            disabled={indexingInProgress}
                            className="btn btn-warning btn-lg text-white"
                        >
                            {indexingInProgress ? (
                                <>
                                    <span className="loading loading-spinner loading-sm mr-2"></span>
                                    Indexing in Progress...
                                </>
                            ) : (
                                <>
                                    <Cog6ToothIcon className="w-5 h-5 mr-2" />
                                    Run Indexing Now
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </DashboardWrapper>
        );
    }

    return (
        <DashboardWrapper>
            <div className="w-full flex min-h-[calc(100vh-64px)] overflow-hidden">
            <div className={`flex-1 flex flex-col gap-4 md:gap-6 p-4 md:p-6 overflow-hidden transition-all duration-300 ${selectedUser ? 'pr-0' : ''}`}>

                {/* Header */}
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-semibold">User Analytics</h1>
                    <div className="flex items-center gap-2">
                        {stats.lastIndexedAt && (
                            <div className="flex items-center gap-2 px-4 py-2 text-sm text-blue-700 bg-blue-100 rounded-lg border border-blue-300">
                                <ClockIcon className="w-5 h-5" />
                                <span>Last indexed {formatDate(stats.lastIndexedAt)}</span>
                            </div>
                        )}
                        <button
                            onClick={handleRunIndexing}
                            disabled={indexingInProgress}
                            className="flex items-center gap-2 px-4 py-2 text-sm border rounded-lg bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                            title="Re-index all user data for faster loading"
                        >
                            {indexingInProgress ? (
                                <>
                                    <span className="loading loading-spinner loading-sm mr-2"></span>
                                    Indexing...
                                </>
                            ) : (
                                <>
                                    <Cog6ToothIcon className="w-4 h-4 mr-2" />
                                    Run Indexing
                                </>
                            )}
                        </button>
                        <button
                            onClick={downloadCSV}
                            disabled={filteredUsers.length === 0}
                            className="flex items-center gap-2 px-4 py-2 text-sm border rounded-lg bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                            title={`Download ${filteredUsers.length} user(s) as CSV`}
                        >
                            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                            Download CSV
                        </button>
                        <button
                            onClick={downloadTopEngagedCSV}
                            disabled={users.length === 0}
                            className="flex items-center gap-2 px-4 py-2 text-sm border rounded-lg bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                            title="Export the top 100 users by Day Posts, regardless of current filters"
                        >
                            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                            Top 100 Engaged
                        </button>
                        <button
                            onClick={downloadGarminUsersCSV}
                            disabled={users.length === 0}
                            className="flex items-center gap-2 px-4 py-2 text-sm border rounded-lg bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                            title="Export name, surname and email for all users with a connected Garmin wearable"
                        >
                            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                            Garmin Users
                        </button>
                        <button
                            onClick={refreshData}
                            disabled={loading}
                            className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-black transition-colors"
                        >
                            <ArrowPathIcon className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-error">
                        <ExclamationCircleIcon className="w-5 h-5" />
                        <span>{error.message}</span>
                    </div>
                )}

                {/* Subscription Stats */}
                <div className="w-full grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-4 shrink-0">
                    {[
                        { value: stats.totalUsers, label: 'Total Users', color: '' },
                        { value: stats.platinumUsers, label: 'Platinum', color: 'text-warning' },
                        { value: stats.premiumUsers, label: 'Premium', color: 'text-success' },
                        { value: stats.coachUsers, label: 'Coach', color: 'text-info' },
                        { value: stats.defaultUsers, label: 'Default', color: 'text-neutral' },
                    ].map(({ value, label, color }) => (
                        <div key={label} className="w-full bg-white rounded-xl flex flex-col p-2 md:p-4 gap-2 md:gap-4 items-start justify-between border">
                            <div className={`text-xl md:text-3xl font-bold ${color}`}>{value}</div>
                            <div className="text-[10px] md:text-xs text-gray-500 uppercase">{label}</div>
                        </div>
                    ))}
                </div>

                {/* Analytics Stats */}
                <div className="w-full grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 md:gap-4 shrink-0">
                    {[
                        { icon: <CalendarDaysIcon className="w-4 h-4 text-emerald-600" />, label: 'Wellness Logs', value: stats.totalDayPosts.toLocaleString(), color: 'text-emerald-700' },
                        { icon: <ChartBarIcon className="w-4 h-4 text-blue-600" />, label: 'Training Logs', value: stats.totalTrainingLogs.toLocaleString(), color: 'text-blue-700' },
                        { icon: <ChartBarIcon className="w-4 h-4 text-orange-600" />, label: 'Activity Logs', value: stats.totalActivityLogs.toLocaleString(), color: 'text-orange-700' },
                        { icon: <Square2StackIcon className="w-4 h-4 text-cyan-600" />, label: 'Sessions', value: stats.totalSessions.toLocaleString(), color: 'text-cyan-700' },
                        { icon: <Cog6ToothIcon className="w-4 h-4 text-purple-600" />, label: 'AI Chats', value: stats.totalAiChats.toLocaleString(), color: 'text-purple-700' },
                        { icon: <EyeIcon className="w-4 h-4 text-pink-600" />, label: 'Social Posts', value: stats.totalPosts.toLocaleString(), color: 'text-pink-700' },
                        { icon: <DevicePhoneMobileIcon className="w-4 h-4 text-indigo-600" />, label: 'Users with Wearables', value: stats.usersWithWearables.toString(), color: 'text-indigo-700' },
                    ].map(({ icon, label, value, color }) => (
                        <div key={label} className="w-full bg-white rounded-xl flex flex-col p-2 md:p-4 gap-2 md:gap-4 items-start justify-between border">
                            <div className="flex items-center gap-2">
                                {icon}
                                <div className="text-[10px] md:text-xs text-gray-500 uppercase line-clamp-1">{label}</div>
                            </div>
                            <div className={`text-xl md:text-3xl font-bold ${color}`}>{value}</div>
                        </div>
                    ))}
                </div>

                {/* Wearables by Device */}
                {stats.wearableBreakdown.length > 0 && (
                    <div className="w-full bg-white rounded-xl border p-3 md:p-4 flex flex-col gap-3 shrink-0">
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase">Users with Wearables by Device</div>
                        <div className="flex flex-wrap gap-2 md:gap-3">
                            {stats.wearableBreakdown.map(({ resource, count }) => (
                                <div key={resource} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border">
                                    <span className="text-base">{getResourceIcon(resource)}</span>
                                    <span className="text-sm text-gray-700">{getResourceDisplayName(resource)}</span>
                                    <span className="text-sm font-bold text-gray-900">{count}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Search and Filters */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-2 w-full">
                        <div className="relative w-full">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={filter.searchTerm || ''}
                                onChange={(e) => setFilter({ ...filter, searchTerm: e.target.value })}
                                className="w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            />
                        </div>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm border rounded-lg bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 relative whitespace-nowrap"
                        >
                            <FunnelIcon className="w-4 h-4 mr-1" />
                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                            {hasActiveFilters && <span className="size-4 rounded-full bg-gray-700 absolute top-0 right-0 translate-x-1/3 -translate-y-1/3 border-2 border-white"></span>}
                        </button>
                        {hasActiveFilters && (
                            <button onClick={clearFilters} className="flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-red-600">
                                <XMarkIcon className="w-4 h-4 mr-1" />
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                {/* Filter Panel */}
                {showFilters && (
                    <div className="bg-gray-50 rounded-xl border p-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="label text-sm font-medium">Subscription Type</label>
                                <select
                                    className={`select select-bordered w-full select-sm ${filter.subscriptionType === undefined ? '' : filter.subscriptionType ? 'text-blue-600 outline' : 'text-gray-400'}`}
                                    value={filter.subscriptionType || ''}
                                    onChange={(e) => setFilter({ ...filter, subscriptionType: e.target.value || undefined })}
                                >
                                    <option value="">All</option>
                                    <option value="platinum">Platinum</option>
                                    <option value="premium">Premium</option>
                                    <option value="coach">Coach</option>
                                    <option value="plus">Plus</option>
                                    <option value="default">Default</option>
                                </select>
                            </div>
                            <div>
                                <label className="label text-sm font-medium">Subscription Via</label>
                                <select
                                    className={`select select-bordered w-full select-sm ${filter.subscriptionVia === undefined ? '' : filter.subscriptionVia ? 'text-blue-600 outline' : 'text-gray-400'}`}
                                    value={filter.subscriptionVia || ''}
                                    onChange={(e) => setFilter({ ...filter, subscriptionVia: e.target.value || undefined })}
                                >
                                    <option value="">All</option>
                                    <option value="stripe">Stripe</option>
                                    <option value="apple">Apple</option>
                                    <option value="google">Google</option>
                                    <option value="admin">Admin</option>
                                    <option value="trial">Trial</option>
                                    <option value="coach">Coach</option>
                                    <option value="default">Default</option>
                                </select>
                            </div>
                            <div>
                                <label className="label text-sm font-medium">Has Wearable</label>
                                <select
                                    className={`select select-bordered w-full select-sm ${filter.hasWearable === undefined ? '' : filter.hasWearable ? 'text-blue-600 outline' : 'text-gray-400'}`}
                                    value={filter.hasWearable === undefined ? '' : filter.hasWearable ? 'yes' : 'no'}
                                    onChange={(e) =>
                                        setFilter({ ...filter, hasWearable: e.target.value === '' ? undefined : e.target.value === 'yes' })
                                    }
                                >
                                    <option value="">All</option>
                                    <option value="yes">Yes</option>
                                    <option value="no">No</option>
                                </select>
                            </div>
                            <div>
                                <label className="label text-sm font-medium">Min Day Posts</label>
                                <input
                                    type="number"
                                    className={`input input-bordered w-full input-sm ${filter.minDayPosts === undefined ? '' : filter.minDayPosts > 0 ? 'text-blue-600 outline' : 'text-gray-400'}`}
                                    placeholder="e.g. 10"
                                    value={filter.minDayPosts ?? ''}
                                    onChange={(e) =>
                                        setFilter({ ...filter, minDayPosts: e.target.value ? parseInt(e.target.value, 10) : undefined })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Users Table */}
                <div className="flex-1 max-h-[calc(100vh-108px)] bg-white rounded-xl border overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="grid grid-cols-[0.5fr_2fr_2fr_2fr_1fr_1fr_1fr_1fr_1fr] gap-2 px-4 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase shrink-0 pr-7">
                        <div>#</div>
                        <div className={`cursor-pointer hover:text-gray-800 hover:font-semibold ${sortField === 'name' ? 'text-gray-800 font-semibold' : ''}`} onClick={() => handleSort('name')}>
                            User
                            <ChevronUpIcon className={`w-4 h-4 inline ml-1 transitional-all ${sortField === 'name' ? 'text-gray-800' : 'text-gray-400 rotate-180'}`} />
                        </div>
                        <div className={`cursor-pointer hover:text-gray-800 hover:font-semibold ${sortField === 'email' ? 'text-gray-800 font-semibold' : ''}`} onClick={() => handleSort('email')}>
                            Email
                            <ChevronUpIcon className={`w-4 h-4 inline ml-1 transitional-all ${sortField === 'email' ? 'text-gray-800' : 'text-gray-400 rotate-180'}`} />
                        </div>
                        <div className={`cursor-pointer hover:text-gray-800 hover:font-semibold ${sortField === 'subscriptionType' ? 'text-gray-800 font-semibold' : ''}`} onClick={() => handleSort('subscriptionType')}>
                            Sub
                            <ChevronUpIcon className={`w-4 h-4 inline ml-1 transitional-all ${sortField === 'subscriptionType' ? 'text-gray-800' : 'text-gray-400 rotate-180'}`} />
                        </div>
                        <div className={`cursor-pointer hover:text-gray-800 hover:font-semibold text-center ${sortField === 'dayPostsCount' ? 'text-gray-800 font-semibold' : ''}`} onClick={() => handleSort('dayPostsCount')}>
                            Day Posts
                            <ChevronUpIcon className={`w-4 h-4 inline ml-1 transitional-all ${sortField === 'dayPostsCount' ? 'text-gray-800' : 'text-gray-400 rotate-180'}`} />
                        </div>
                        <div className={`cursor-pointer hover:text-gray-800 hover:font-semibold text-center ${sortField === 'postsCount' ? 'text-gray-800 font-semibold' : ''}`} onClick={() => handleSort('postsCount')}>
                            Posts
                            <ChevronUpIcon className={`w-4 h-4 inline ml-1 transitional-all ${sortField === 'postsCount' ? 'text-gray-800' : 'text-gray-400 rotate-180'}`} />
                        </div>
                        <div className={`cursor-pointer hover:text-gray-800 hover:font-semibold text-center ${sortField === 'aiChatsCount' ? 'text-gray-800 font-semibold' : ''}`} onClick={() => handleSort('aiChatsCount')}>
                            AI Chats
                            <ChevronUpIcon className={`w-4 h-4 inline ml-1 transitional-all ${sortField === 'aiChatsCount' ? 'text-gray-800' : 'text-gray-400 rotate-180'}`} />
                        </div>
                        <div className={`cursor-pointer hover:text-gray-800 hover:font-semibold text-center ${sortField === 'wearableDevicesCount' ? 'text-gray-800 font-semibold' : ''}`} onClick={() => handleSort('wearableDevicesCount')}>
                            Wearables
                            <ChevronUpIcon className={`w-4 h-4 inline ml-1 transitional-all ${sortField === 'wearableDevicesCount' ? 'text-gray-800' : 'text-gray-400 rotate-180'}`} />
                        </div>
                        <div className={`cursor-pointer hover:text-gray-800 hover:font-semibold text-center ${sortField === 'sessionsCount' ? 'text-gray-800 font-semibold' : ''}`} onClick={() => handleSort('sessionsCount')}>
                            Sessions
                            <ChevronUpIcon className={`w-4 h-4 inline ml-1 transitional-all ${sortField === 'sessionsCount' ? 'text-gray-800' : 'text-gray-400 rotate-180'}`} />
                        </div>
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto min-h-0">
                        {filteredUsers.length === 0 ? (
                            <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
                                {filter.searchTerm || hasActiveFilters
                                    ? 'No users found matching your filters.'
                                    : 'No indexed users found. Click "Run Indexing" to index user data.'}
                            </div>
                        ) : (
                            filteredUsers.map((user, index) => (
                                <Link to={`/users?user=${user.id}`} key={user.id} className={`grid grid-cols-[0.5fr_2fr_2fr_2fr_1fr_1fr_1fr_1fr_1fr] gap-2 px-4 py-3 border-b hover:bg-gray-50 items-center transition-colors cursor-pointer ${selectedUser?.id === user.id ? 'bg-primary/5 border-l-2 border-l-primary' : ''}`}>
                                    <div className="text-sm text-gray-500">{index + 1}</div>
                                    <div className="flex items-center gap-2 min-w-0">
                                        {user.imageUrl ? (
                                            <img
                                                src={user.imageUrl}
                                                alt={`${user.name}'s avatar`}
                                                className="w-8 h-8 rounded-full object-cover shrink-0"
                                            />
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-xs font-medium shrink-0">
                                                {user.name.charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <div className="font-medium text-sm truncate">{user.name} {user.surname}</div>
                                            <div className="text-xs text-gray-500 truncate lowercase">@{user.username || 'no-username'}</div>
                                        </div>
                                    </div>
                                    <div className="text-sm text-gray-600 truncate">{user.email}</div>
                                    <div>
                                        <div className="flex items-center gap-1">
                                            <span className={`inline-flex px-2 py-0.5 text-xs rounded-full capitalize ${
                                                user.subscriptionType === 'platinum' ? 'bg-yellow-100 text-yellow-700'
                                                : user.subscriptionType === 'premium' ? 'bg-blue-100 text-blue-700'
                                                : user.subscriptionType === 'coach' ? 'bg-green-100 text-green-700'
                                                : user.subscriptionType === 'plus' ? 'bg-purple-100 text-purple-700'
                                                : 'bg-gray-100 text-gray-700'
                                            }`}>
                                                {user.subscriptionType || 'default'}
                                            </span>
                                            <span className="text-xs text-gray-400">via {user.subscriptionVia || 'default'}</span>
                                        </div>
                                    </div>
                                    <div className="text-sm font-medium text-center">{user.dayPostsCount}</div>
                                    <div className="text-sm text-center">
                                        <span className={`font-medium ${user.postsCount > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                                            {user.postsCount}
                                        </span>
                                    </div>
                                    <div className="text-sm text-center">
                                        <span className={`font-medium ${user.aiChatsCount > 0 ? 'text-purple-600' : 'text-gray-400'}`}>
                                            {user.aiChatsCount}
                                        </span>
                                    </div>
                                    <div className="text-sm text-center">
                                        <span className={`font-medium ${user.wearableDevicesCount > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                            {user.wearableDevicesCount}
                                        </span>
                                    </div>
                                    <div className="text-sm text-center">
                                        <span className={`font-medium ${user.sessionsCount > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                                            {user.sessionsCount}
                                        </span>
                                    </div>
                                </Link>
                            ))
                        )}
                    </div>
                </div>
            </div>  {/* end inner flex-col */}
            <UserDetailSidebar
                user={selectedUser ? {
                    uid: selectedUser.id,
                    name: selectedUser.name,
                    surname: selectedUser.surname,
                    email: selectedUser.email,
                    username: selectedUser.username,
                    imageUrl: selectedUser.imageUrl,
                    role: selectedUser.role,
                    sport: selectedUser.sport,
                    subscription: {
                        subscriptionType: selectedUser.subscriptionType || 'default',
                        subscriptionVia: selectedUser.subscriptionVia || 'default',
                        lastUpdated: new Date(),
                        subscriptionChangeMessage: '',
                        subscriptionChangeRead: true,
                        userId: selectedUser.id,
                    },
                } as unknown as UserData & { uid: string } : null}
                onClose={() => setSelectedUser(null)}
            />
            </div>  {/* end outer flex row */}
        </DashboardWrapper>
    );
};

export default UserAnalyticsPage;
